import React, { useMemo, useState } from "react";
import {
  Box, Typography, Button, Chip, Stack, TextField, InputAdornment,
  Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Menu, MenuItem, Switch, FormControlLabel, alpha, useTheme, useMediaQuery,
  Snackbar, Alert,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  useGetMyMessageTemplatesQuery,
  useCreateMyMessageTemplateMutation,
  useUpdateMyMessageTemplateMutation,
  useActivateMyMessageTemplateMutation,
  useDeactivateMyMessageTemplateMutation,
  useResetMyMessageTemplateMutation,
  useDeleteMyMessageTemplateMutation,
} from "../../Api/attoDeskApi";
import { StepUpSpinner } from "../../Components/Loader";
import useUser from "../../Core/hook/useUser";
import { useConfirm, confirmPresets } from "../../Components/Confirm";

// Palette aligned with the redesigned Messages page.
const C = {
  blue:  { fg: "#1d4ed8", bg: alpha("#3b82f6", 0.10), border: alpha("#3b82f6", 0.28) },
  teal:  { fg: "#0f766e", bg: alpha("#14b8a6", 0.10), border: alpha("#14b8a6", 0.28) },
  green: { fg: "#15803d", bg: alpha("#22c55e", 0.10), border: alpha("#22c55e", 0.28) },
  indigo:{ fg: "#4338ca", bg: alpha("#6366f1", 0.10), border: alpha("#6366f1", 0.28) },
  amber: { fg: "#a16207", bg: alpha("#eab308", 0.10), border: alpha("#eab308", 0.28) },
  // Rose = Inactive/destructive. bg deepened from 0.08 → 0.16 and
  // border from 0.28 → 0.55 so the Inactive chip reads as clearly
  // RED (not grey/washed-out) even at 22px chip height.
  rose:  { fg: "#b91c1c", bg: alpha("#ef4444", 0.16), border: alpha("#ef4444", 0.55) },
  navy:  { fg: "#0f172a" },
};

const PLACEHOLDERS = ["studentName", "parentName", "staffName"] as const;
type PlaceholderKey = typeof PLACEHOLDERS[number];

const SAMPLE: Record<PlaceholderKey, string> = {
  studentName: "Test Student",
  parentName:  "Demo Parent",
  staffName:   "Dinesh Ratno",
};

const resolvePreview = (s: string): string =>
  s.replaceAll("{studentName}", SAMPLE.studentName)
   .replaceAll("{parentName}",  SAMPLE.parentName)
   .replaceAll("{staffName}",   SAMPLE.staffName);

const extractPlaceholders = (s: string): string[] => {
  const out = new Set<string>();
  const re = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) out.add(m[1]);
  return Array.from(out);
};

type TemplateRow = {
  id: number;
  name: string;
  subject_template: string;
  body_template: string;
  active: boolean;
  default_key: string | null;
  owner_user_id: number | null;
  sort_order: number;
  is_system_default: boolean;
  is_mine: boolean;
  can_reset: boolean;
  updated_at: string | null;
};

type FilterKey = "all" | "active" | "inactive" | "default" | "custom";

const MessageTemplates: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { canCreate } = useUser();
  const canEdit = canCreate("Message");

  const { data, isLoading, isFetching } = useGetMyMessageTemplatesQuery();
  const [createTpl] = useCreateMyMessageTemplateMutation();
  const [updateTpl] = useUpdateMyMessageTemplateMutation();
  const [activateTpl] = useActivateMyMessageTemplateMutation();
  const [deactivateTpl] = useDeactivateMyMessageTemplateMutation();
  const [resetTpl] = useResetMyMessageTemplateMutation();
  const [deleteTpl] = useDeleteMyMessageTemplateMutation();
  const confirm = useConfirm();

  const rows: TemplateRow[] = useMemo(() => ((data as any)?.data?.rows ?? []) as TemplateRow[], [data]);
  const counts = (data as any)?.data?.counts ?? { total: 0, active: 0, inactive: 0, default: 0, custom: 0 };

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState<TemplateRow | null>(null);
  const [toast, setToast] = useState<{ kind: "success" | "error"; msg: string } | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "active"   && !r.active) return false;
      if (filter === "inactive" &&  r.active) return false;
      if (filter === "default"  && !r.is_system_default) return false;
      if (filter === "custom"   &&  r.is_system_default) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q)
          || r.subject_template.toLowerCase().includes(q)
          || r.body_template.toLowerCase().includes(q);
    });
  }, [rows, search, filter]);

  const runAction = async (label: string, promise: Promise<any>) => {
    try {
      await promise;
      setToast({ kind: "success", msg: label });
    } catch (err: any) {
      const message = err?.data?.message || `Could not ${label.toLowerCase()}.`;
      setToast({ kind: "error", msg: message });
    }
  };

  const hasActiveFilters = Boolean(search) || filter !== "all";
  const clearFilters = () => { setSearch(""); setFilter("all"); };

  const kpis = [
    { key: "active",   label: "Active Templates",   value: counts.active,   tone: "green"  as const, Icon: CheckCircleOutlineOutlinedIcon, helper: "Available in composer" },
    { key: "custom",   label: "Custom Templates",   value: counts.custom,   tone: "indigo" as const, Icon: EditNoteOutlinedIcon,           helper: "Owned by you" },
    { key: "default",  label: "Default Templates",  value: counts.default,  tone: "blue"   as const, Icon: ArticleOutlinedIcon,            helper: "Step Up defaults" },
  ];

  return (
    <Box sx={{ bgcolor: "#f4f7fb", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1720, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {/* ── HERO ─────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "#fff", borderRadius: "16px", px: { xs: 2.5, md: 3 }, py: 2,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 2, mb: 2.5, flexWrap: "wrap",
            border: "1px solid rgba(15,23,42,0.06)",
            background: "linear-gradient(135deg, #ffffff 0%, #fafcff 55%, #f2f8fb 100%)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px",
                bgcolor: C.blue.bg, color: C.blue.fg,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ArticleOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "1.4px", color: "#64748b", textTransform: "uppercase" }}>
                Staff Portal
              </Typography>
              <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: C.navy.fg, lineHeight: 1.2 }}>
                Message Templates
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Create and customize reusable messages for parents and guardians.
              </Typography>
            </Box>
          </Stack>

          {canEdit && (
            <Button
              variant="contained" startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              sx={{
                bgcolor: C.navy.fg, color: "#fff", fontWeight: 800, textTransform: "none",
                borderRadius: "12px", px: 2.25, py: 1, fontSize: 13.5,
                boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
                "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              New Template
            </Button>
          )}
        </Box>

        {/* ── KPI ROW ──────────────────────────────────────── */}
        <Box sx={{ display: "grid", gap: 1.5, mb: 2.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
          {kpis.map((k) => {
            const tone = C[k.tone];
            return (
              <Box key={k.key} sx={{
                bgcolor: "#fff", borderRadius: "14px", p: { xs: 1.5, md: 2 },
                border: "1px solid rgba(15,23,42,0.06)",
                display: "flex", alignItems: "center", gap: 1.5, minHeight: 82,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: "10px",
                  bgcolor: tone.bg, color: tone.fg,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <k.Icon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: tone.fg, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {isLoading ? "—" : Number(k.value)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: C.navy.fg, textTransform: "uppercase", mt: 0.5 }}>
                    {k.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    {k.helper}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── FILTER TOOLBAR ──────────────────────────────── */}
        <Box sx={{
          bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.06)",
          p: { xs: 1.25, md: 1.5 }, mb: 2,
          display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap",
        }}>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, flex: 1, minWidth: 0 }}>
            {(["all", "active", "inactive", "default", "custom"] as FilterKey[]).map((f) => {
              const active = filter === f;
              const label = f === "all" ? "All" : f[0].toUpperCase() + f.slice(1);
              return (
                <Chip
                  key={f} label={label}
                  onClick={() => setFilter(f)}
                  sx={{
                    height: 32, fontSize: 12.5, fontWeight: 700, borderRadius: "10px", cursor: "pointer",
                    bgcolor: active ? C.navy.fg : "#f1f5f9",
                    color: active ? "#fff" : "#334155",
                    border: active ? "none" : "1px solid rgba(15,23,42,0.06)",
                    "&:hover": { bgcolor: active ? "#1e293b" : "#e2e8f0" },
                  }}
                />
              );
            })}
          </Stack>
          <TextField
            size="small"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flexBasis: { xs: "100%", sm: 260, md: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px", height: 38, bgcolor: "#f8fafc",
                "& fieldset": { borderColor: "rgba(15,23,42,0.08)" },
                "&.Mui-focused fieldset": { borderColor: C.blue.fg },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}><ClearOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {/* ── LIST ────────────────────────────────────────── */}
        {isLoading ? (
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: "14px", bgcolor: "rgba(15,23,42,0.05)" }} />
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{
            bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.06)",
            minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 3, py: 4 }}>
              <Box sx={{
                width: 60, height: 60, borderRadius: "50%",
                bgcolor: hasActiveFilters ? C.blue.bg : C.teal.bg,
                color: hasActiveFilters ? C.blue.fg : C.teal.fg,
                display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5,
              }}>
                {hasActiveFilters ? <SearchOutlinedIcon sx={{ fontSize: 28 }} /> : <ArticleOutlinedIcon sx={{ fontSize: 28 }} />}
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.navy.fg }}>
                {hasActiveFilters ? "No templates match your filters" : "No message templates yet"}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", maxWidth: 340, mt: 0.5 }}>
                {hasActiveFilters
                  ? "Try a different filter or clear your search."
                  : "Create your first reusable message for parents and guardians."}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {hasActiveFilters && (
                  <Button size="small" onClick={clearFilters} startIcon={<ClearOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 700, color: C.blue.fg, "&:hover": { bgcolor: alpha("#3b82f6", 0.08) } }}>
                    Clear filters
                  </Button>
                )}
                {!hasActiveFilters && canEdit && (
                  <Button size="small" variant="contained" onClick={() => setCreating(true)} startIcon={<AddIcon />}
                    sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 800, bgcolor: C.navy.fg, "&:hover": { bgcolor: "#1e293b" } }}>
                    New Template
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
            {filtered.map((r) => (
              <TemplateCard
                key={r.id} row={r} canEdit={canEdit}
                onPreview={() => setPreviewing(r)}
                onEdit={() => setEditing(r)}
                onActivate={() => runAction("Template activated.", activateTpl({ id: r.id }).unwrap())}
                onDeactivate={() => runAction("Template deactivated.", deactivateTpl({ id: r.id }).unwrap())}
                onReset={() => runAction("Template reset to Step Up default.", resetTpl({ id: r.id }).unwrap())}
                onDelete={async () => {
                  // Reuse the app's single ConfirmProvider dialog (see
                  // Components/Confirm/README.md). Never window.confirm.
                  const ok = await confirm({
                    ...confirmPresets.deletePermanent("template"),
                    title: "Delete Template?",
                    message: "Are you sure you want to delete this template? This action cannot be undone.",
                    confirmText: "Delete",
                  });
                  if (!ok) return;
                  await runAction("Template deleted.", deleteTpl({ id: r.id }).unwrap());
                }}
              />
            ))}
          </Box>
        )}

        {isFetching && !isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <StepUpSpinner size="small" />
          </Box>
        )}
      </Box>

      {(creating || editing) && canEdit && (
        <TemplateEditor
          existing={editing}
          fullScreen={isMobile}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={(msg) => {
            setCreating(false); setEditing(null);
            setToast({ kind: "success", msg });
          }}
          onError={(m) => setToast({ kind: "error", msg: m })}
          save={async (payload) => {
            if (editing) {
              await updateTpl({ id: editing.id, ...payload }).unwrap();
              return "Template updated.";
            } else {
              await createTpl(payload).unwrap();
              return "Template created.";
            }
          }}
        />
      )}

      {previewing && (
        <PreviewDialog row={previewing} onClose={() => setPreviewing(null)} fullScreen={isMobile} />
      )}

      <Snackbar
        open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast?.kind || "success"} variant="filled" onClose={() => setToast(null)} sx={{ fontWeight: 700 }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
const TemplateCard: React.FC<{
  row: TemplateRow; canEdit: boolean;
  onPreview: () => void; onEdit: () => void;
  onActivate: () => void; onDeactivate: () => void; onReset: () => void;
  onDelete: () => void;
}> = ({ row, canEdit, onPreview, onEdit, onActivate, onDeactivate, onReset, onDelete }) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const activeChip = row.active
    ? { bg: C.green.bg, fg: C.green.fg, border: C.green.border, label: "Active", Icon: CheckCircleOutlineOutlinedIcon }
    : { bg: C.rose.bg,  fg: C.rose.fg,  border: C.rose.border,  label: "Inactive", Icon: PauseCircleOutlineOutlinedIcon };
  const kindChip = row.is_system_default
    ? { bg: C.blue.bg,   fg: C.blue.fg,   border: C.blue.border,   label: "Default" }
    : { bg: C.indigo.bg, fg: C.indigo.fg, border: C.indigo.border, label: row.is_mine ? "Custom" : "Custom" };
  const placeholders = useMemo(
    () => Array.from(new Set([...extractPlaceholders(row.subject_template), ...extractPlaceholders(row.body_template)])),
    [row],
  );

  return (
    <Box sx={{
      bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.06)",
      borderLeft: `3px solid ${row.is_system_default ? C.blue.fg : C.indigo.fg}`,
      p: 2, display: "flex", flexDirection: "column", gap: 1.25, minHeight: 200,
    }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.navy.fg, lineHeight: 1.2 }}>
            {row.name}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
            <Chip
              size="small" icon={<activeChip.Icon sx={{ fontSize: 14 }} />} label={activeChip.label}
              sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: activeChip.bg, color: activeChip.fg, border: `1px solid ${activeChip.border}`, "& .MuiChip-icon": { color: activeChip.fg } }}
            />
            <Chip
              size="small" label={kindChip.label}
              sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: kindChip.bg, color: kindChip.fg, border: `1px solid ${kindChip.border}` }}
            />
          </Stack>
        </Box>
        {canEdit && (
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More">
              <MoreVertOutlinedIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); onPreview(); }}>Preview</MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); onEdit(); }}>
                {row.is_system_default ? "Customize" : "Edit"}
              </MenuItem>
              {row.active ? (
                <MenuItem onClick={() => { setMenuAnchor(null); onDeactivate(); }}>Deactivate</MenuItem>
              ) : (
                <MenuItem onClick={() => { setMenuAnchor(null); onActivate(); }}>Activate</MenuItem>
              )}
              {row.can_reset && (
                <MenuItem onClick={() => { setMenuAnchor(null); onReset(); }}>Reset to Default</MenuItem>
              )}
              {/* Delete is only offered for caller-owned custom
                  templates (never for system defaults). Backend
                  double-checks: system defaults → 422, other staff's
                  customs → 403. Visually destructive (red + icon). */}
              {!row.is_system_default && row.is_mine && (
                <MenuItem
                  onClick={() => { setMenuAnchor(null); onDelete(); }}
                  sx={{ color: C.rose.fg, "& .MuiSvgIcon-root": { color: C.rose.fg }, fontWeight: 700 }}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 18, mr: 1 }} />
                  Delete
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Stack>

      <Box>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: "#64748b", textTransform: "uppercase" }}>
          Subject
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy.fg, mt: 0.25 }}>
          {row.subject_template}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: "#64748b", textTransform: "uppercase" }}>
          Message
        </Typography>
        <Typography sx={{
          fontSize: 12.5, color: "#334155", mt: 0.25, whiteSpace: "pre-wrap",
          display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {row.body_template}
        </Typography>
      </Box>

      {placeholders.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
          {placeholders.map((p) => (
            <Chip key={p} size="small" label={`{${p}}`}
              sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569", borderRadius: "6px" }} />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
        <Button size="small" startIcon={<PreviewOutlinedIcon sx={{ fontSize: 16 }} />} onClick={onPreview}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#334155" }}>
          Preview
        </Button>
        {canEdit && (
          <Button size="small" startIcon={<EditNoteOutlinedIcon sx={{ fontSize: 16 }} />} onClick={onEdit}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: C.blue.fg }}>
            {row.is_system_default ? "Customize" : "Edit"}
          </Button>
        )}
        {row.can_reset && (
          <Button size="small" startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 16 }} />} onClick={onReset}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#64748b" }}>
            Reset
          </Button>
        )}
        <FormControlLabel
          sx={{ ml: "auto" }}
          control={
            <Switch
              size="small"
              checked={row.active}
              onChange={() => (row.active ? onDeactivate() : onActivate())}
              disabled={!canEdit}
            />
          }
          label={<Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Active</Typography>}
        />
      </Stack>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
const TemplateEditor: React.FC<{
  existing: TemplateRow | null;
  fullScreen: boolean;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
  save: (payload: { name: string; subject_template: string; body_template: string; active: boolean }) => Promise<string>;
}> = ({ existing, fullScreen, onClose, onSaved, onError, save }) => {
  const [name, setName] = useState(existing?.name ?? "");
  const [subject, setSubject] = useState(existing?.subject_template ?? "");
  const [body, setBody] = useState(existing?.body_template ?? "");
  const [active, setActive] = useState<boolean>(existing?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [focusField, setFocusField] = useState<"subject" | "body">("body");

  const insert = (token: PlaceholderKey) => {
    const chunk = `{${token}}`;
    if (focusField === "subject") setSubject((s) => `${s}${chunk}`);
    else setBody((s) => `${s}${chunk}`);
  };

  const unknown = useMemo(() => {
    const found = new Set([...extractPlaceholders(subject), ...extractPlaceholders(body), ...extractPlaceholders(name)]);
    const bad: string[] = [];
    found.forEach((t) => { if (!PLACEHOLDERS.includes(t as PlaceholderKey)) bad.push(t); });
    return bad;
  }, [subject, body, name]);

  const canSave = name.trim().length >= 2 && subject.trim().length >= 3 && body.trim().length >= 3 && unknown.length === 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const msg = await save({ name: name.trim(), subject_template: subject, body_template: body, active });
      onSaved(msg);
    } catch (err: any) {
      onError(err?.data?.message || "Could not save template.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "16px" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          px: 3, py: 2.25, position: "relative", color: "#fff",
          background: `linear-gradient(135deg, ${C.navy.fg} 0%, #1e293b 100%)`,
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EditNoteOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", opacity: 0.75, textTransform: "uppercase" }}>
                Staff Portal · Message Templates
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.2, mt: 0.25 }}>
                {existing ? (existing.is_system_default ? "Customize Template" : "Edit Template") : "New Template"}
              </Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.9, mt: 0.5 }}>
                {existing?.is_system_default
                  ? "Saving creates a customized copy for your own use. The Step Up default is unchanged for other staff."
                  : "Reusable message for parents and guardians."}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} disabled={saving} aria-label="Close"
            sx={{ position: "absolute", top: 12, right: 12, color: "#fff", bgcolor: "rgba(255,255,255,0.14)", "&:hover": { bgcolor: "rgba(255,255,255,0.24)" }, width: 30, height: 30 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, md: 3 }, pt: { xs: 3, md: 3.5 }, pb: { xs: 2, md: 3 }, bgcolor: "#f8fafc", overflowX: "visible" }}>
        <Stack spacing={1.5} sx={{ pt: 1, overflow: "visible" }}>
          <TextField
            fullWidth size="small" label="Template Name *"
            value={name} onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 120 }}
            InputLabelProps={{ shrink: true }}
            helperText={`${name.length} / 120`}
            sx={{ mt: 0.5, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <TextField
            fullWidth size="small" label="Subject *"
            value={subject} onChange={(e) => setSubject(e.target.value)}
            onFocus={() => setFocusField("subject")}
            inputProps={{ maxLength: 191 }}
            InputLabelProps={{ shrink: true }}
            helperText={`${subject.length} / 191`}
            sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <TextField
            fullWidth multiline minRows={8} maxRows={16} size="small" label="Message *"
            value={body} onChange={(e) => setBody(e.target.value)}
            onFocus={() => setFocusField("body")}
            inputProps={{ maxLength: 5000 }}
            InputLabelProps={{ shrink: true }}
            helperText={`${body.length} / 5000`}
            sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#fff", border: "1px solid rgba(15,23,42,0.06)" }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
              Available placeholders
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
              {PLACEHOLDERS.map((p) => (
                <Tooltip key={p} title={`Insert into ${focusField}`} arrow>
                  <Chip
                    label={`{${p}}`}
                    onClick={() => insert(p as PlaceholderKey)}
                    size="small"
                    sx={{
                      height: 26, fontSize: 11.5, fontWeight: 700, borderRadius: "8px",
                      bgcolor: C.blue.bg, color: C.blue.fg,
                      border: `1px solid ${C.blue.border}`,
                      "&:hover": { bgcolor: alpha("#3b82f6", 0.18) },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1 }}>
              Click a chip to insert it into the currently focused field. Unknown placeholders will be rejected on save.
            </Typography>
            {unknown.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
                Unknown placeholder: {unknown.map((u) => `{${u}}`).join(", ")}. Supported: {PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}.
              </Alert>
            )}
          </Box>

          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Active (shown in New Message composer)</Typography>}
          />
        </Stack>

        <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", bgcolor: C.teal.bg, border: `1px solid ${C.teal.border}` }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: C.teal.fg, textTransform: "uppercase", mb: 0.5 }}>
            Live preview
          </Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy.fg }}>
            {resolvePreview(subject || "—")}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#334155", whiteSpace: "pre-wrap", mt: 0.5 }}>
            {resolvePreview(body || "—")}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff" }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 700 }}>Cancel</Button>
        <Button
          variant="contained" disabled={!canSave || saving}
          startIcon={saving ? <StepUpSpinner size="small" /> : undefined}
          onClick={handleSave}
          sx={{ textTransform: "none", fontWeight: 800, bgcolor: C.navy.fg, px: 2.25, borderRadius: "10px", "&:hover": { bgcolor: "#1e293b" } }}
        >
          {saving ? "Saving…" : "Save Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ═════════════════════════════════════════════════════════════
const PreviewDialog: React.FC<{ row: TemplateRow; onClose: () => void; fullScreen: boolean }> = ({ row, onClose, fullScreen }) => (
  <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}
    PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "16px" } }}>
    <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
      <PreviewOutlinedIcon sx={{ color: C.blue.fg }} />
      Preview · {row.name}
      <IconButton onClick={onClose} sx={{ ml: "auto" }}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Subject
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.navy.fg, mt: 0.5, mb: 2 }}>
        {resolvePreview(row.subject_template)}
      </Typography>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Message
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", mt: 0.5, mb: 2 }}>
        {resolvePreview(row.body_template)}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "text.disabled", fontStyle: "italic" }}>
        Sample values: studentName → {SAMPLE.studentName}, parentName → {SAMPLE.parentName}, staffName → {SAMPLE.staffName}.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default MessageTemplates;
